import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  Select,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  ComposedChart
} from "recharts";
import { 
  MdAttachMoney, 
  MdArrowDropDown, 
  MdCalendarToday,
  MdTrendingUp,
  MdShowChart,
  MdPieChart,
  MdVolunteerActivism,
} from "react-icons/md";
import { CurrencyContext } from "../../analysis";

const ALL_MONTHS = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

function emptyMonthlySeries(year) {
  return ALL_MONTHS.map((name) => ({
    name,
    gelir: 0,
    tip: 0,
    year,
  }));
}

export default function RevenueAnalysis({ monthlyRevenues = [], yearlyRevenue = 0 }) {
  const currencySymbol = useContext(CurrencyContext);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [revenueData, setRevenueData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const brandColor = useColorModeValue("brand.500", "brand.400");
  const barColor = useColorModeValue("#4299E1", "#63B3ED");
  const lineColor = useColorModeValue("#805AD5", "#B794F4");
  const gridColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const subtleText = useColorModeValue("gray.600", "gray.400");
  
  // Ay sıralama fonksiyonu
  const getMonthOrder = (monthName) => {
    const monthOrder = {
      'Ocak': 1, 'Şubat': 2, 'Mart': 3, 'Nisan': 4, 'Mayıs': 5, 'Haziran': 6,
      'Temmuz': 7, 'Ağustos': 8, 'Eylül': 9, 'Ekim': 10, 'Kasım': 11, 'Aralık': 12
    };
    return monthOrder[monthName] || 0;
  };

  // Veri yükleme fonksiyonu - gerçek verileri kullan
  useEffect(() => {
    setIsLoading(true);
    
    // Backend'den ay bazlı gelir varsa kullan; yoksa grafik/tabloda sıfır göster (mock yok)
    if (monthlyRevenues && monthlyRevenues.length > 0) {
      // Backend'den gelen verilerdeki mevcut yılları bul ve state'e kaydet
      const years = [...new Set(monthlyRevenues.map(item => item.year))].sort((a, b) => b - a);
      setAvailableYears(years);
      
      const maxYear = years.length > 0 ? years[0] : new Date().getFullYear();
      
      // Eğer seçilen yıl için veri yoksa, en son yılı kullan
      const yearToUse = years.includes(Number(selectedYear)) 
        ? Number(selectedYear) 
        : maxYear;
      
      // Eğer yıl değiştiyse, state'i güncelle
      if (yearToUse !== Number(selectedYear) && yearToUse === maxYear) {
        setSelectedYear(yearToUse.toString());
      }
      
      // Seçilen yıl için verileri formatla ve sırala
      // Tüm ayları dahil et (veri olsun ya da olmasın)
      const allMonths = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                          'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
      
      // Önce backend'den gelen verileri filtrele (sadece seçilen yıl)
      const filteredRevenues = monthlyRevenues.filter(item => item.year === yearToUse);
      
      let formattedData = allMonths.map(monthName => {
        const monthData = filteredRevenues.find(
          item => item.month === monthName
        );
        return {
          name: monthName,
          gelir: monthData ? monthData.revenue : 0,
          tip: monthData ? (monthData.tipAmount || 0) : 0,
          monthOrder: getMonthOrder(monthName),
          year: yearToUse
        };
      }).sort((a, b) => a.monthOrder - b.monthOrder)
        .map(item => ({
          name: item.name,
          gelir: item.gelir,
          tip: item.tip,
          year: item.year
        }));
      
      setRevenueData(formattedData);
      
      // Karşılaştırma verileri için önceki yıl
      const currentYearForComparison = yearToUse;
      if (currentYearForComparison > 2021) {
        const prevYear = currentYearForComparison - 1;
        const prevYearData = monthlyRevenues
          .filter(item => item.year === prevYear)
          .map(item => ({
            name: item.month,
            gelir: item.revenue,
            monthOrder: getMonthOrder(item.month)
          }))
          .sort((a, b) => a.monthOrder - b.monthOrder)
          .map(item => ({
            name: item.name,
            gelir: item.gelir
          }));
        
        if (prevYearData.length > 0) {
          setComparisonData({
            data: prevYearData,
            label: prevYear.toString()
          });
        } else {
          setComparisonData(null);
        }
      } else {
        setComparisonData(null);
      }
    } else {
      setAvailableYears([]);
      setComparisonData(null);
      setRevenueData(emptyMonthlySeries(Number(selectedYear)));
    }
    
    setIsLoading(false);
  }, [selectedYear, monthlyRevenues]);
  
  // Toplam gelir hesaplama
  const calculateTotal = (data) => {
    return data ? data.reduce((sum, item) => sum + item.gelir, 0) : 0;
  };
  
  // Toplam tip geliri hesaplama
  const calculateTotalTip = (data) => {
    return data ? data.reduce((sum, item) => sum + (item.tip || 0), 0) : 0;
  };
  
  // Büyüme oranı hesaplama
  const calculateGrowth = () => {
    if (!comparisonData) return null;
    
    const currentTotal = calculateTotal(revenueData);
    const prevTotal = calculateTotal(comparisonData.data);
    
    if (prevTotal === 0) return 100;
    return ((currentTotal - prevTotal) / prevTotal * 100).toFixed(1);
  };
  
  // En yüksek gelirli dönemi bulma
  const findPeakPeriod = () => {
    if (!revenueData || revenueData.length === 0) return { name: "Veri yok", gelir: 0 };
    
    return revenueData.reduce((max, item) => 
      max.gelir > item.gelir ? max : item, { gelir: 0 });
  };
  
  // Ortalama gelir hesaplama (sadece veri olan aylar)
  const calculateAverage = () => {
    if (!revenueData || revenueData.length === 0) return 0;
    const monthsWithData = revenueData.filter(item => item.gelir > 0);
    if (monthsWithData.length === 0) return 0;
    return (calculateTotal(monthsWithData) / monthsWithData.length).toFixed(0);
  };
  
  const growth = calculateGrowth();
  const peakPeriod = findPeakPeriod();
  const averageRevenue = calculateAverage();
  // Gerçek veriler varsa onları kullan, yoksa hesaplanan toplamı kullan
  // Seçilen yıl için toplam gelir hesapla (revenueData'dan, çünkü zaten seçilen yıl için filtrelenmiş)
  const totalRevenue = calculateTotal(revenueData);
  const totalTipRevenue = calculateTotalTip(revenueData);
  
  return (
    <Card>
      <Flex justify="space-between" align="center" mb={6}>
        <Flex align="center">
          <Icon as={MdAttachMoney} color={brandColor} boxSize={6} mr={2} />
          <Text color={textColor} fontSize="lg" fontWeight="700">
            Gelir Analizi
          </Text>
        </Flex>
        
        <Flex>
          <Menu>
            <MenuButton as={Button} rightIcon={<MdArrowDropDown />} variant="outline" size="sm" colorScheme="brand">
              {selectedYear}
            </MenuButton>
            <MenuList>
              {availableYears.length > 0 
                ? availableYears.map(year => (
                    <MenuItem key={year} onClick={() => setSelectedYear(year.toString())}>
                      {year}
                    </MenuItem>
                  ))
                : [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map(year => (
                    <MenuItem key={year} onClick={() => setSelectedYear(year.toString())}>
                      {year}
                    </MenuItem>
                  ))
              }
            </MenuList>
          </Menu>
        </Flex>
      </Flex>

      <Box mb={6} w="100%">
        <ResponsiveContainer width="100%" height={350}>
          {comparisonData ? (
            <LineChart
              key={`line-${selectedYear}`}
              data={revenueData.map((item, index) => ({
                ...item,
                prevGelir: comparisonData.data[index]?.gelir || 0
              }))}
              margin={{ top: 10, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis 
                dataKey="name" 
                tick={{ fill: textColor, fontSize: 11 }} 
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fill: textColor }} />
              <RechartsTooltip 
                formatter={(value, name) => {
                  if (name === 'gelir') {
                    return [`${currencySymbol}${Number(value).toLocaleString('tr-TR')}`, `${selectedYear} Gelir`];
                  } else if (name === 'prevGelir') {
                    return [`${currencySymbol}${Number(value).toLocaleString('tr-TR')}`, `${comparisonData.label} Gelir`];
                  } else if (name === 'tip') {
                    return [`${currencySymbol}${Number(value).toLocaleString('tr-TR')}`, 'Bahşiş'];
                  }
                  return [`${currencySymbol}${Number(value).toLocaleString('tr-TR')}`, name];
                }}
                labelFormatter={(label) => `Ay: ${label}`}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="gelir" 
                name={`${selectedYear} Gelir`}
                stroke={barColor} 
                activeDot={{ r: 6 }} 
                strokeWidth={3}
                dot={{ r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="prevGelir" 
                name={`${comparisonData.label} Gelir`}
                stroke={lineColor} 
                strokeWidth={3}
                strokeDasharray="5 5"
                dot={{ r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="tip" 
                name="Bahşiş"
                stroke="pink" 
                strokeWidth={2}
                strokeDasharray="3 3"
                dot={{ r: 3 }}
              />
            </LineChart>
          ) : (
            <BarChart
              key={`bar-${selectedYear}`}
              data={revenueData}
              margin={{ top: 10, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis 
                dataKey="name" 
                tick={{ fill: textColor, fontSize: 11 }} 
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fill: textColor }} />
              <RechartsTooltip 
                formatter={(value) => [`${currencySymbol}${Number(value).toLocaleString('tr-TR')}`, 'Gelir']}
                labelFormatter={(label) => `Ay: ${label}`}
              />
              <Legend />
              <Bar 
                dataKey="gelir" 
                fill={barColor} 
                radius={[4, 4, 0, 0]} 
                name="Gelir"
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing="20px">
        <Stat bg={useColorModeValue("gray.50", "navy.700")} p={4} borderRadius="lg" boxShadow="sm">
          <Flex align="center" mb={2}>
            <Icon as={MdAttachMoney} color={brandColor} boxSize={5} mr={2} />
            <StatLabel>Yıllık Toplam Gelir</StatLabel>
          </Flex>
          <StatNumber>{currencySymbol}{totalRevenue.toLocaleString()}</StatNumber>
          {growth && (
            <StatHelpText>
              <StatArrow type={growth > 0 ? "increase" : "decrease"} />
              %{Math.abs(growth)} ({Number(selectedYear)-1}'e göre)
            </StatHelpText>
          )}
        </Stat>
        
        <Stat bg={useColorModeValue("gray.50", "navy.700")} p={4} borderRadius="lg" boxShadow="sm">
          <Flex align="center" mb={2}>
            <Icon as={MdVolunteerActivism} color="pink.500" boxSize={5} mr={2} />
            <StatLabel>Yıllık Toplam Bahşiş</StatLabel>
          </Flex>
          <StatNumber color="pink.500">{currencySymbol}{totalTipRevenue.toLocaleString()}</StatNumber>
          <StatHelpText>
            {totalRevenue > 0 ? `%${((totalTipRevenue / totalRevenue) * 100).toFixed(1)}` : '0%'} toplam gelirden
          </StatHelpText>
        </Stat>
        
        <Stat bg={useColorModeValue("gray.50", "navy.700")} p={4} borderRadius="lg" boxShadow="sm">
          <Flex align="center" mb={2}>
            <Icon as={MdTrendingUp} color="green.500" boxSize={5} mr={2} />
            <StatLabel>Ortalama Gelir</StatLabel>
          </Flex>
          <StatNumber>{currencySymbol}{Number(averageRevenue).toLocaleString()}</StatNumber>
          <StatHelpText>
            Veri olan ayların ortalaması
          </StatHelpText>
        </Stat>
        
        <Stat bg={useColorModeValue("gray.50", "navy.700")} p={4} borderRadius="lg" boxShadow="sm">
          <Flex align="center" mb={2}>
            <Icon as={MdShowChart} color="purple.500" boxSize={5} mr={2} />
            <StatLabel>En Yüksek Gelir</StatLabel>
          </Flex>
          <StatNumber>{currencySymbol}{peakPeriod.gelir.toLocaleString()}</StatNumber>
          <StatHelpText>
            {peakPeriod.name}
          </StatHelpText>
        </Stat>
        
        <Stat bg={useColorModeValue("gray.50", "navy.700")} p={4} borderRadius="lg" boxShadow="sm">
          <Flex align="center" mb={2}>
            <Icon as={MdPieChart} color="orange.500" boxSize={5} mr={2} />
            <StatLabel>Gelir Dağılımı</StatLabel>
          </Flex>
          <StatNumber>
            12 ay
          </StatNumber>
          <StatHelpText>
            {selectedYear}
          </StatHelpText>
        </Stat>
      </SimpleGrid>
    </Card>
  );
} 